import { useState, useMemo, useEffect } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { toast } from "sonner";
import { Plus, Trash2, Loader2, PackageSearch } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useStoreLookups, useSubmitStoreIndent, useCheckDraftStoreIndentStock } from "@/features/store-indents/api";
import { LocationHierarchyPicker } from "@/features/store-indents/LocationHierarchyPicker";
import { ITEM_KIND_LABELS, type StoreIndentKind } from "@/features/store-indents/types";

const KINDS = ["asset", "spare", "consumable", "stationery"] as const;
import { useStoresByBranch } from "@/features/masters/api";
import { useCurrentUser } from "@/hooks/useCurrentUser";

export const Route = createFileRoute("/_authenticated/store-indents/new")({
  component: NewStoreIndent,
  validateSearch: (s: Record<string, unknown>) => ({ kind: (s.kind as string) || "spare" }),
});

type Line = {
  item_kind: StoreIndentKind;
  spare_id?: string;
  asset_id?: string;
  location_id?: string;
  item_name?: string;
  qty_requested: number;
  remarks?: string;
  // "Request New Item": a named item that is not in the catalogue / not a registered asset (procured via SI -> PI shortage)
  is_new_item?: boolean;
  specification?: string;
  justification?: string;
};

function NewStoreIndent() {
  const navigate = useNavigate();
  const { kind: initialKind } = Route.useSearch();
  const { data: lookups } = useStoreLookups();
  const submit = useSubmitStoreIndent();
  // Default kind for new lines (from ?kind=); each line carries its own item_kind so one document can mix kinds.
  const defaultKind: StoreIndentKind = (KINDS as readonly string[]).includes(initialKind) ? (initialKind as StoreIndentKind) : "spare";
  const { data: me } = useCurrentUser();
  const [branchId, setBranchId] = useState("");
  const [deptId, setDeptId] = useState("");
  // A department user raises indents for their own assigned branch and department only: auto-filled and not editable
  const roles = (me?.roles ?? []) as string[];
  const lockedToAssignment = !!me?.profile?.branch_id && !!me?.profile?.department_id
    && roles.length > 0 && roles.every((r) => ["dept_user", "hod"].includes(r));
  useEffect(() => {
    if (lockedToAssignment) {
      setBranchId(me!.profile!.branch_id as string);
      setDeptId(me!.profile!.department_id as string);
    }
  }, [lockedToAssignment, me?.profile?.branch_id, me?.profile?.department_id]); // eslint-disable-line
  const [sourceStoreId, setSourceStoreId] = useState("");
  const [remarks, setRemarks] = useState("");
  const [lines, setLines] = useState<Line[]>([{ item_kind: defaultKind, qty_requested: 1 }]);
  // Requested (destination) location: one common location for every line (lines may override) or a location per line.
  const [locMode, setLocMode] = useState<"common" | "per_line">("common");
  const [commonLoc, setCommonLoc] = useState<string | undefined>(undefined);
  const [stockDialogOpen, setStockDialogOpen] = useState(false);
  const [stockResults, setStockResults] = useState<any[]>([]);

  const checkDraftStock = useCheckDraftStoreIndentStock();

  const { data: storesForBranch = [] } = useStoresByBranch(branchId || null);

  const departments = useMemo(() => (lookups?.departments ?? []).filter((d) => !branchId || d.branch_id === branchId), [lookups, branchId]);
  const locations = useMemo(() => (lookups?.locations ?? []).filter((l) => !branchId || l.branch_id === branchId), [lookups, branchId]);
  const sparesFor = (k: StoreIndentKind) => (lookups?.spares ?? []).filter((s) => (s.item_type ?? "spare") === k);
  const hasQtyLines = lines.some((l) => l.item_kind !== "asset");
  const assetsInStore = useMemo(() => (lookups?.assetsInStore ?? []).filter((a) => !branchId || a.branch_id === branchId), [lookups, branchId]);

  // Automatically update empty locations when department is selected
  useEffect(() => {
    if (!deptId) return;
    const deptLocation = locations.find((l) => l.kind === "department" && l.department_id === deptId);
    if (deptLocation) {
      setLines((prev) => prev.map((l) => (!l.location_id ? { ...l, location_id: deptLocation.id } : l)));
      setCommonLoc((c) => c ?? deptLocation.id);
    }
  }, [deptId, locations]);

  // Changing the common location moves every line that still follows it; explicit per-line overrides are kept.
  const changeCommon = (id: string | undefined) => {
    setLines((prev) => prev.map((l) => (!l.location_id || l.location_id === commonLoc ? { ...l, location_id: id } : l)));
    setCommonLoc(id);
  };
  const applyCommonToAll = () => setLines((prev) => prev.map((l) => ({ ...l, location_id: commonLoc })));

  const addLine = () => {
    const deptLocation = deptId ? locations.find((l) => l.kind === "department" && l.department_id === deptId) : undefined;
    setLines((s) => [...s, { item_kind: s[s.length - 1]?.item_kind ?? defaultKind, qty_requested: 1, location_id: (locMode === "common" ? commonLoc : undefined) ?? deptLocation?.id }]);
  };
  const removeLine = (idx: number) => setLines((s) => s.filter((_, i) => i !== idx));
  const updateLine = (idx: number, patch: Partial<Line>) =>
    setLines((s) => s.map((l, i) => (i === idx ? { ...l, ...patch } : l)));
  // Changing a line kind clears references that belong to the previous kind.
  const changeKind = (idx: number, k: StoreIndentKind) =>
    updateLine(idx, { item_kind: k, spare_id: undefined, asset_id: undefined, item_name: undefined, qty_requested: k === "asset" && !lines[idx].is_new_item ? 1 : lines[idx].qty_requested || 1 });
  const changeSource = (idx: number, isNew: boolean) =>
    updateLine(idx, { is_new_item: isNew, spare_id: undefined, asset_id: undefined, item_name: undefined, qty_requested: lines[idx].qty_requested || 1 });
  // Legacy header kind: first line kind (the backend validates each line by its own item_kind).
  const headerKind: StoreIndentKind = lines[0]?.item_kind ?? defaultKind;

  const onSubmit = async () => {
    if (!branchId) return toast.error("Branch required");
    if (!deptId) return toast.error("Department required");
    if (lines.length === 0) return toast.error("Add at least one item");
    if (lines.some((l) => !l.location_id)) return toast.error("Select the requested location for every line (or set a common location)");
    for (const l of lines) {
      if (l.is_new_item) {
        if (!l.item_name?.trim()) return toast.error("New item request: enter the item name");
        if (!(l.qty_requested > 0) || !Number.isInteger(Number(l.qty_requested))) return toast.error("New item request: quantity must be a positive whole number");
      } else if (l.item_kind === "asset") {
        if (!l.asset_id) return toast.error("Pick an asset from the store");
        if (!l.location_id) return toast.error("Target location required for asset allocation");
      } else {
        if (!l.spare_id) return toast.error("Choose an existing item for each line, or use Request new item");
        if (!(l.qty_requested > 0) || !Number.isInteger(Number(l.qty_requested))) return toast.error("Quantity must be a positive whole number");
      }
    }
    try {
      const res = await submit.mutateAsync({
        branch_id: branchId,
        department_id: deptId,
        source_store_id: sourceStoreId || undefined,
        kind: headerKind,
        remarks,
        items: lines.map((l) => (l.item_kind === "asset" && !l.is_new_item ? { ...l, qty_requested: 1 } : l)),
      });
      toast.success(`Store indent ${res.store_indent_no} submitted`);
      navigate({ to: "/store-indents/$id", params: { id: res.id } });
    } catch (e) { toast.error((e as Error).message); }
  };

  const handleCheckStock = async () => {
    if (!branchId) return toast.error("Branch required to check stock");
    if (lines.length === 0) return toast.error("Add at least one item");
    
    // Validate items
    const qtyLines = lines.filter((l) => l.item_kind !== "asset");
    for (const l of qtyLines) {
      if (!l.spare_id && !(l.item_name?.trim())) return toast.error("Each line needs an item");
    }

    try {
      const results = await checkDraftStock.mutateAsync({
        branch_id: branchId,
        source_store_id: sourceStoreId || undefined,
        items: qtyLines,
      });
      setStockResults(results);
      setStockDialogOpen(true);
    } catch (e) {
      toast.error((e as Error).message);
    }
  };

  return (
    <div className="max-w-5xl mx-auto space-y-4">
      <h1 className="text-2xl font-semibold tracking-tight">New Store Indent</h1>

      <Card>
        <CardHeader><CardTitle>Header</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <Label>Branch *</Label>
            <Select value={branchId} onValueChange={(v) => { setBranchId(v); setDeptId(""); }} disabled={lockedToAssignment}>
              <SelectTrigger className={lockedToAssignment ? "bg-slate-50 cursor-not-allowed opacity-80" : ""}><SelectValue placeholder="Choose" /></SelectTrigger>
              <SelectContent>
                {(lookups?.branches ?? []).map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Department *</Label>
            <Select value={deptId} onValueChange={setDeptId} disabled={!branchId || lockedToAssignment}>
              <SelectTrigger className={lockedToAssignment ? "bg-slate-50 cursor-not-allowed opacity-80" : ""}><SelectValue placeholder="Choose" /></SelectTrigger>
              <SelectContent>
                {departments.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Source Store</Label>
            <Select
              value={sourceStoreId || "__all__"}
              onValueChange={(v) => setSourceStoreId(v === "__all__" ? "" : v)}
              disabled={!branchId}
            >
              <SelectTrigger>
                <SelectValue placeholder={branchId ? "Any store (optional)" : "Select branch first"} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">— Any store —</SelectItem>
                {storesForBranch.map((s) => (
                  <SelectItem key={s.id} value={s.id}>
                    {s.name} <span className="text-xs text-muted-foreground ml-1">[{s.code}]</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-2">
            <Label>Remarks</Label>
            <Textarea rows={2} value={remarks} onChange={(e) => setRemarks(e.target.value)} />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Items</CardTitle>
          <div className="flex gap-2">
            {hasQtyLines && (
              <Button size="sm" variant="secondary" onClick={handleCheckStock} disabled={checkDraftStock.isPending || !branchId}>
                {checkDraftStock.isPending ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <PackageSearch className="h-4 w-4 mr-1" />}
                Check Stock Availability
              </Button>
            )}
            <Button size="sm" variant="outline" onClick={addLine}><Plus className="h-4 w-4 mr-1" />Add line</Button>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="space-y-3 border-b bg-muted/30 p-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex flex-wrap items-center gap-3">
                <Label className="text-sm font-medium">Requested location</Label>
                <div className="inline-flex rounded-md border bg-background p-0.5 text-xs">
                  {([["common", "Common location"], ["per_line", "Different per item"]] as const).map(([k, t]) => (
                    <button key={k} type="button" onClick={() => setLocMode(k)}
                      className={`rounded px-3 py-1.5 ${locMode === k ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>{t}</button>
                  ))}
                </div>
              </div>
              <span className="text-xs text-muted-foreground">Where the items are needed - not where stock is taken from.</span>
            </div>
            {locMode === "common" ? (
              <div className="flex flex-wrap items-center gap-3">
                <div className="min-w-[320px] flex-1 max-w-3xl">
                  <LocationHierarchyPicker locations={locations} branchId={branchId} departmentId={deptId} value={commonLoc}
                    onChange={changeCommon} compact hideDepartment={true} />
                </div>
                <Button type="button" size="sm" variant="outline" onClick={applyCommonToAll} disabled={!commonLoc}>Apply to all lines</Button>
                <span className="text-xs text-muted-foreground">New lines use this location; you can still change any line below.</span>
              </div>
            ) : (
              <p className="text-xs text-muted-foreground">Choose the requested location on each line in the table below.</p>
            )}
          </div>
          <div className="max-w-full overflow-x-auto">
          <Table className="min-w-[1280px]">
            <TableHeader><TableRow>
              <TableHead className="w-[140px]">Kind</TableHead>
              <TableHead className="min-w-[220px]">Item / Asset</TableHead>
              <TableHead className="min-w-[200px]">Or Item Name</TableHead>
              <TableHead className="w-24">Qty</TableHead>
              <TableHead className="min-w-[440px]">Requested location</TableHead>
              <TableHead className="min-w-[220px]">Remarks</TableHead>
              <TableHead></TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {lines.map((l, i) => (
                <TableRow key={i}>
                  <TableCell>
                    <Select value={l.item_kind} onValueChange={(v) => changeKind(i, v as StoreIndentKind)}>
                      <SelectTrigger className="w-[130px]"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {KINDS.map((k) => <SelectItem key={k} value={k}>{ITEM_KIND_LABELS[k]}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Select value={l.is_new_item ? "new" : "existing"} onValueChange={(v) => changeSource(i, v === "new")}>
                      <SelectTrigger className="mt-1 w-[130px] text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="existing">Existing item</SelectItem>
                        <SelectItem value="new">Request new item</SelectItem>
                      </SelectContent>
                    </Select>
                  </TableCell>
                  {l.item_kind === "asset" && !l.is_new_item ? (
                    <>
                      <TableCell colSpan={2}>
                        <Select value={l.asset_id ?? ""} onValueChange={(v) => updateLine(i, { asset_id: v })}>
                          <SelectTrigger className="min-w-[260px]"><SelectValue placeholder="Choose asset" /></SelectTrigger>
                          <SelectContent className="max-h-72">
                            {assetsInStore.length === 0 && <div className="p-2 text-xs text-muted-foreground">No unallocated assets in this branch.</div>}
                            {assetsInStore.map((a) => (
                              <SelectItem key={a.id} value={a.id}>{a.asset_code} · {a.name}{a.serial_number ? ` (SN ${a.serial_number})` : ""}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell className="text-muted-foreground">1</TableCell>
                      <TableCell className="min-w-[440px]">
                        <LocationHierarchyPicker
                          locations={locations}
                          branchId={branchId}
                          departmentId={deptId}
                          value={l.location_id}
                          onChange={(id) => updateLine(i, { location_id: id })}
                          compact
                          hideDepartment={true}
                        />
                      </TableCell>
                    </>
                  ) : (
                    <>
                      {l.is_new_item ? (
                        <TableCell colSpan={2} className="min-w-[420px]">
                          <div className="space-y-1">
                            <Input value={l.item_name ?? ""} onChange={(e) => updateLine(i, { item_name: e.target.value })} placeholder={`New ${ITEM_KIND_LABELS[l.item_kind]} name *`} />
                            <Input value={l.specification ?? ""} onChange={(e) => updateLine(i, { specification: e.target.value })} placeholder="Description / specifications" />
                            <Input value={l.justification ?? ""} onChange={(e) => updateLine(i, { justification: e.target.value })} placeholder="Reason / justification" />
                            <p className="text-[11px] text-muted-foreground">Not in the catalogue: no stock or asset is created. Store will procure it through the Procurement Shortage.</p>
                          </div>
                        </TableCell>
                      ) : (<>
                      <TableCell>
                        <Select value={l.spare_id ?? "__none"} onValueChange={(v) => updateLine(i, { spare_id: v === "__none" ? undefined : v, item_name: undefined })}>
                          <SelectTrigger className="min-w-[200px]"><SelectValue placeholder={`Choose ${l.item_kind}`} /></SelectTrigger>
                          <SelectContent className="max-h-72">
                            <SelectItem value="__none">— None —</SelectItem>
                            {sparesFor(l.item_kind).map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell className="min-w-[120px] text-xs text-muted-foreground">{l.spare_id ? "Existing item" : "Choose an item"}</TableCell>
                      </>)}
                      <TableCell>
                        <Input
                          type="number"
                          step="1"
                          min="1"
                          value={l.qty_requested}
                          onKeyDown={(e) => {
                            if (e.key === "-" || e.key === "e" || e.key === "." || e.key === ",") e.preventDefault();
                          }}
                          onChange={(e) => {
                            const val = e.target.value;
                            if (val === "") {
                              updateLine(i, { qty_requested: "" as any });
                              return;
                            }
                            const parsed = parseInt(val, 10);
                            if (Number.isNaN(parsed)) return;
                            updateLine(i, { qty_requested: Math.max(0, parsed) });
                          }}
                          className="w-24"
                        />
                      </TableCell>
                      <TableCell className="min-w-[440px]">
                        <LocationHierarchyPicker
                          locations={locations}
                          branchId={branchId}
                          departmentId={deptId}
                          value={l.location_id}
                          onChange={(id) => updateLine(i, { location_id: id })}
                          compact
                          hideDepartment={true}
                        />
                      </TableCell>
                    </>
                  )}
                  <TableCell className="min-w-[220px]"><Input value={l.remarks ?? ""} onChange={(e) => updateLine(i, { remarks: e.target.value })} placeholder="Optional" /></TableCell>
                  <TableCell className="sticky right-0 w-12 bg-background"><Button size="sm" variant="ghost" onClick={() => removeLine(i)}><Trash2 className="h-4 w-4 text-destructive" /></Button></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={() => navigate({ to: "/store-indents" })}>Cancel</Button>
        <Button onClick={onSubmit} disabled={submit.isPending}>Submit Store Indent</Button>
      </div>

      <Dialog open={stockDialogOpen} onOpenChange={setStockDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-auto">
          <DialogHeader><DialogTitle>Stock Availability</DialogTitle></DialogHeader>
          <div className="space-y-4 pt-2">
            {stockResults.map((res, i) => (
              <div key={i} className="p-3 border rounded-md">
                <div className="font-semibold mb-2">{res.spare_name} <span className="text-muted-foreground font-normal ml-2">Req: {res.requested_qty}</span></div>
                {res.spare_id ? (
                  <div className="grid grid-cols-3 gap-2 text-sm mb-3">
                    <div>Total On Hand: <b>{res.total_on_hand}</b></div>
                    <div>Total Reserved: <b>{res.total_reserved}</b></div>
                    <div className="text-primary">Available: <b>{res.available}</b></div>
                  </div>
                ) : (
                  <div className="text-sm text-muted-foreground mb-3">Free text item — no stock to check</div>
                )}
                {res.stores && res.stores.length > 0 && (
                  <div className="text-sm">
                    <div className="font-medium bg-muted px-2 py-1 mb-1 rounded">Per Store</div>
                    {res.stores.map((s: any, idx: number) => (
                      <div key={idx} className="flex justify-between px-2 py-1 border-b last:border-0">
                        <span>{s.store_location}</span>
                        <span>{s.available}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
